import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";

import style from "./bulk-search-modal.scss";
import Modal from "components/modal/Modal";
import buttonStyle from "styles/buttons.scss";
import {
    BACKEND_SEPARATOR,
    isJoinedLengthValid,
    isSearchTermCountValid,
    MAX_JOINED_CHARACTER_COUNT,
    MAX_SEARCH_TERM_COUNT,
    parseSearchTerms,
} from "utils/bulkSearchTerms";

import testIds from "testIds.json";

type DelimiterType = "newline" | "comma" | "semicolon" | "tab" | "space" | "custom";

const PRESET_DELIMITER_CHARACTERS: Record<Exclude<DelimiterType, "custom">, string> = {
    newline: "\n",
    comma: ",",
    semicolon: ";",
    tab: "\t",
    space: " ",
};

const PRESET_DELIMITER_OPTIONS: ReadonlyArray<{
    type: Exclude<DelimiterType, "custom">;
    localizationKey: string;
}> = [
    { type: "newline", localizationKey: "BulkSearchModal.delimiterNewline" },
    { type: "comma", localizationKey: "BulkSearchModal.delimiterComma" },
    { type: "semicolon", localizationKey: "BulkSearchModal.delimiterSemicolon" },
    { type: "tab", localizationKey: "BulkSearchModal.delimiterTab" },
    { type: "space", localizationKey: "BulkSearchModal.delimiterSpace" },
];

interface Props {
    onClose: () => void;
    initialValue: string;
    onApply: (joinedSearchTerms: string) => void;
}

function deriveActiveDelimiter(delimiterType: DelimiterType, customDelimiter: string): string | undefined {
    if (delimiterType === "custom") {
        return customDelimiter === "" ? undefined : customDelimiter;
    }
    return PRESET_DELIMITER_CHARACTERS[delimiterType];
}

// TODO BCC-6375 Review styling. Now it's just whatever Claude provided.

const BulkSearchModal = (props: Props): JSX.Element => {
    const { t } = useTranslation();
    const [textareaValue, setTextareaValue] = React.useState<string>(() =>
        parseSearchTerms(props.initialValue, BACKEND_SEPARATOR).join("\n")
    );
    const [delimiterType, setDelimiterType] = React.useState<DelimiterType>("newline");
    const [customDelimiter, setCustomDelimiter] = React.useState<string>("");

    const activeDelimiter = deriveActiveDelimiter(delimiterType, customDelimiter);
    const searchTerms = activeDelimiter == null ? [] : parseSearchTerms(textareaValue, activeDelimiter);
    const searchTermCountValid = isSearchTermCountValid(searchTerms);
    const joinedLengthValid = isJoinedLengthValid(searchTerms);
    const hasExistingFilters = props.initialValue.trim() !== "";
    const canApplyNewFilters =
        activeDelimiter !== undefined && searchTerms.length > 0 && searchTermCountValid && joinedLengthValid;
    const canClearExistingFilters = hasExistingFilters && searchTerms.length === 0;
    const canApply = canApplyNewFilters || canClearExistingFilters;

    const handleApply = () => {
        props.onApply(searchTerms.join(BACKEND_SEPARATOR));
    };

    return (
        <Modal isOpen={true} hideModal={props.onClose} modalTitle={t("BulkSearchModal.title")}>
            <div className={style.container}>
                <label className={style.label} htmlFor="bulkSearchTextarea">
                    {t("BulkSearchModal.textareaLabel")}
                </label>
                <textarea
                    id="bulkSearchTextarea"
                    className={style.textarea}
                    value={textareaValue}
                    onChange={(event) => setTextareaValue(event.target.value)}
                    data-testid={testIds.workArea.report.erasure.bulkSearchDialog.searchInput}
                />

                <fieldset className={style.delimiterGroup}>
                    <legend className={style.label}>{t("BulkSearchModal.delimiterLabel")}</legend>
                    {PRESET_DELIMITER_OPTIONS.map(({ type, localizationKey }) => (
                        <label key={type} className={style.delimiterOption}>
                            <input
                                type="radio"
                                name="bulkSearchDelimiter"
                                checked={delimiterType === type}
                                onChange={() => setDelimiterType(type)}
                                data-testid={testIds.workArea.report.erasure.bulkSearchDialog.delimiterRadioButton}
                                data-type={type}
                            />
                            {t(localizationKey)}
                        </label>
                    ))}
                    <label className={style.delimiterOption}>
                        <input
                            type="radio"
                            name="bulkSearchDelimiter"
                            checked={delimiterType === "custom"}
                            onChange={() => setDelimiterType("custom")}
                            data-testid={testIds.workArea.report.erasure.bulkSearchDialog.delimiterRadioButton}
                            data-type="custom"
                        />
                        {t("BulkSearchModal.delimiterCustom")}
                        <input
                            type="text"
                            className={style.customDelimiterInput}
                            value={customDelimiter}
                            onChange={(event) => {
                                setCustomDelimiter(event.target.value);
                                setDelimiterType("custom");
                            }}
                            placeholder={t("BulkSearchModal.customDelimiterPlaceholder")}
                            disabled={delimiterType !== "custom"}
                            data-testid={testIds.workArea.report.erasure.bulkSearchDialog.customDelimiterInput}
                        />
                    </label>
                </fieldset>

                <div>
                    <div className={style.previewHeader}>
                        <span className={style.label}>{t("BulkSearchModal.preview")}</span>
                        <span
                            className={searchTermCountValid ? style.searchTermCount : style.searchTermCountError}
                            data-testid={testIds.workArea.report.erasure.bulkSearchDialog.searchTermCountLabel}
                        >
                            {t("BulkSearchModal.searchTermCount", { count: searchTerms.length })}
                        </span>
                    </div>
                    {!searchTermCountValid && (
                        <div
                            className={style.errorMessage}
                            data-testid={testIds.workArea.report.erasure.bulkSearchDialog.searchTermCountErrorLabel}
                        >
                            {t("BulkSearchModal.searchTermCountExceeded", { max: MAX_SEARCH_TERM_COUNT })}
                        </div>
                    )}
                    {!joinedLengthValid && (
                        <div
                            className={style.errorMessage}
                            data-testid={testIds.workArea.report.erasure.bulkSearchDialog.joinedLengthErrorLabel}
                        >
                            {t("BulkSearchModal.joinedLengthExceeded", { max: MAX_JOINED_CHARACTER_COUNT })}
                        </div>
                    )}
                    <div
                        className={classNames(style.chips, {
                            [style.chipsError]: !searchTermCountValid || !joinedLengthValid,
                        })}
                    >
                        {searchTerms.map((searchTerm, index) => (
                            <span
                                key={`${index}-${searchTerm}`}
                                className={style.chip}
                                data-testid={testIds.workArea.report.erasure.bulkSearchDialog.resolvedSearchTermLabel}
                            >
                                {searchTerm}
                            </span>
                        ))}
                    </div>
                </div>

                <div className={style.actions}>
                    <button
                        type="button"
                        className={buttonStyle.primaryButton}
                        disabled={!canApply}
                        onClick={handleApply}
                        data-testid={testIds.workArea.report.erasure.bulkSearchDialog.applyButton}
                    >
                        {t("BulkSearchModal.apply")}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default BulkSearchModal;
